import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { Search as SearchIcon, Filter, ArrowLeft, ArrowRight } from "lucide-react";

import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";

import ProductCard from "@/components/product/ProductCard";
import { Product } from "@/services/types/domain";
import { productsApi } from "@/services/api/products"; // Debe exponer searchProducts(query, filters)

// Mapeo de opciones de orden para UI → backend
const SORT_OPTIONS: { value: string; label: string; backend?: ProductFilters["sort"] }[] = [
    { value: "relevance", label: "Relevancia" }, // backend: undefined (el /search ya ordena por relevancia)
    { value: "created_desc", label: "Novedades", backend: "created_desc" },
    { value: "price_asc", label: "Precio: menor a mayor", backend: "price_asc" },
    { value: "price_desc", label: "Precio: mayor a menor", backend: "price_desc" },
    { value: "rating_desc", label: "Mejor calificados", backend: "rating_desc" },
    { value: "name_asc", label: "Nombre A–Z", backend: "name_asc" },
    { value: "name_desc", label: "Nombre Z–A", backend: "name_desc" },
];

// Tipos mínimos (si ya existen, importe desde tus types)
export interface ProductFilters {
    category_id?: number;
    seller_id?: number;
    min_price?: number | string;
    max_price?: number | string;
    brand?: string;
    attributes?: Record<string, string>;
    is_featured?: boolean;
    status?: string;
    sort?: "name_asc" | "name_desc" | "price_asc" | "price_desc" | "created_desc" | "created_asc" | "rating_desc";
    page?: number;
    per_page?: number;
    in_stock?: boolean;
}

const SearchPage: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    // --- Params de URL ---
    const q = params.get("q") || "";
    const sortUi = params.get("sort") || "relevance";
    const page = Number(params.get("page") || 1);
    const per_page = Number(params.get("per_page") || 24);
    const category_id = params.get("category_id") ? Number(params.get("category_id")) : undefined;
    const brand = params.get("brand") || "";
    const min_price = params.get("min_price") || "";
    const max_price = params.get("max_price") || "";
    const in_stock = params.get("in_stock") ? ["1", "true", "yes"].includes((params.get("in_stock") || "").toLowerCase()) : undefined;

    const sortBackend = SORT_OPTIONS.find(o => o.value === sortUi)?.backend; // undefined si relevance

    // --- Estado local ---
    const [query, setQuery] = useState(q);
    const [filters, setFilters] = useState({
        brand,
        min_price,
        max_price,
        in_stock: Boolean(in_stock),
    });

    const [results, setResults] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // meta paginación del backend adaptado
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(page);
    const [lastPage, setLastPage] = useState(1);

    const hasQuery = useMemo(() => (q ?? "").trim().length > 0, [q]);

    // Mantén sincronizado el input con la URL
    useEffect(() => setQuery(q), [q]);
    useEffect(() => setCurrentPage(page), [page]);

    // --- Fetch con tu servicio searchProducts ---
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await productsApi.searchProducts(q, {
                    sort: sortBackend, // undefined → relevancia por defecto
                    page,
                    per_page,
                    category_id,
                    brand: brand || undefined,
                    min_price: min_price || undefined,
                    max_price: max_price || undefined,
                    in_stock,
                } as ProductFilters);

                if (!mounted) return;
                // FrontendPaginatedResponse<Product>
                setResults(response.data || []);
                setTotal(response.meta?.total ?? 0);
                setCurrentPage(response.meta?.current_page ?? page);
                setLastPage(response.meta?.last_page ?? 1);
            } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || "No se pudieron cargar los resultados";
                if (!mounted) return;
                setError(msg);
                toast.error(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [q, sortUi, page, per_page, category_id, brand, min_price, max_price, in_stock, sortBackend]);

    // --- Handlers de UI ---
    const updateQueryString = (patch: Record<string, string | number | boolean | undefined | null>) => {
        const next = new URLSearchParams(params.toString());
        Object.entries(patch).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "") next.delete(k);
            else next.set(k, String(v));
        });
        navigate(`/search?${next.toString()}`);
    };

    const onSubmitSearch = (e: React.FormEvent) => {
        e.preventDefault();
        updateQueryString({ q: query, page: 1 });
    };

    const onChangeSort = (value: string) => {
        updateQueryString({ sort: value, page: 1 });
    };

    const onApplyFilters = (e: React.FormEvent) => {
        e.preventDefault();
        updateQueryString({
            brand: filters.brand || undefined,
            min_price: filters.min_price || undefined,
            max_price: filters.max_price || undefined,
            in_stock: filters.in_stock ? 1 : undefined,
            page: 1,
        });
    };

    const goToPage = (p: number) => {
        if (p < 1 || p > lastPage) return;
        updateQueryString({ page: p });
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-7xl">
                {/* Barra de búsqueda */}
                <div className="mb-4">
                    <form onSubmit={onSubmitSearch} className="max-w-3xl mx-auto">
                        <Input
                            value={query}
                            onChange={(e: any) => setQuery(e.target.value)}
                            placeholder="Buscar productos…"
                            leftIcon={<SearchIcon className="w-5 h-5" />}
                            fullWidth
                        />
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                                <span>Ordenar:</span>
                                <select
                                    className="rounded-md border-gray-300 text-sm"
                                    onChange={(e) => onChangeSort(e.target.value)}
                                    value={sortUi}
                                >
                                    {SORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            {hasQuery && (
                                <span>
                                    <Badge variant="outline">{total}</Badge> resultados para <strong>{q}</strong>
                                </span>
                            )}
                        </div>
                    </form>
                </div>

                {/* Filtros rápidos */}
                <form onSubmit={onApplyFilters} className="mb-4 rounded-xl border bg-white p-3 sm:p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-gray-900 font-semibold"><Filter className="w-4 h-4" /> Filtros</div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <Input
                            label="Marca"
                            value={filters.brand}
                            onChange={(e: any) => setFilters(s => ({ ...s, brand: e.target.value }))}
                            placeholder="Ej. Samsung"
                            fullWidth
                        />
                        <Input
                            label="Precio mín."
                            type="number"
                            value={filters.min_price}
                            onChange={(e: any) => setFilters(s => ({ ...s, min_price: e.target.value }))}
                            placeholder="0"
                            fullWidth
                        />
                        <Input
                            label="Precio máx."
                            type="number"
                            value={filters.max_price}
                            onChange={(e: any) => setFilters(s => ({ ...s, max_price: e.target.value }))}
                            placeholder="1000"
                            fullWidth
                        />
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300"
                                    checked={filters.in_stock}
                                    onChange={(e) => setFilters(s => ({ ...s, in_stock: e.target.checked }))}
                                />
                                En stock
                            </label>
                        </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                        <Button type="submit" size="sm">Aplicar filtros</Button>
                    </div>
                </form>

                {/* Resultados */}
                {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {Array.from({ length: Math.min(per_page, 12) }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="h-64 rounded-2xl bg-white border animate-pulse"
                                initial={{ opacity: 0.8 }}
                                animate={{ opacity: 1 }}
                            />
                        ))}
                    </div>
                ) : error ? (
                    <div className="rounded-xl border bg-white p-6 text-center text-red-600">{error}</div>
                ) : results.length === 0 ? (
                    <div className="rounded-xl border bg-white p-8 text-center">
                        <div className="mb-2 text-lg font-semibold">Sin resultados</div>
                        <p className="text-gray-600">Prueba con otras palabras clave o ajusta los filtros.</p>
                    </div>
                ) : (
                    <>
                        <motion.div
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
                            initial="hidden"
                            animate="visible"
                            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
                        >
                            {results.map((p) => (
                                <motion.div key={p.id} variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}>
                                    <ProductCard product={p} />
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Paginación simple */}
                        <div className="mt-6 flex items-center justify-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} leftIcon={<ArrowLeft className="w-4 h-4" />}>
                                Anterior
                            </Button>
                            <span className="text-sm text-gray-700">
                                Página <strong>{currentPage}</strong> de <strong>{lastPage}</strong>
                            </span>
                            <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= lastPage} rightIcon={<ArrowRight className="w-4 h-4" />}>
                                Siguiente
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
